import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NikLogo } from "@/components/Logo";
import { useKids } from "@/context/KidsContext";
import api from "@/lib/api";
import { Play, Lock, Sparkles } from "lucide-react";

export default function KidsHome() {
  const [items, setItems] = useState(null);
  const { exitKids } = useKids();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/content", { params: { type: "kids" } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const featured = items?.[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#10245c] via-[#0e1a3f] to-[#0B0E14]">
      {/* Kids header */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-4 md:px-10 py-4 bg-[#10245c]/70 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center gap-2">
          <NikLogo size={30} showText={false} />
          <span className="font-display font-extrabold text-lg">
            <span className="text-white">KALAM</span><span className="text-[var(--nik-gold)]">PRIME</span>
            <span className="ml-2 text-sm px-2 py-0.5 rounded-full bg-[var(--nik-gold)] text-black align-middle">KIDS</span>
          </span>
        </div>
        <button
          data-testid="exit-kids-btn"
          onClick={exitKids}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
        >
          <Lock size={15} /> Exit Kids
        </button>
      </header>

      <div className="px-4 md:px-10 py-8">
        <div className="flex items-center gap-2 text-[var(--nik-gold)] fade-up">
          <Sparkles size={18} />
          <span className="font-semibold tracking-wide">Welcome to the fun zone!</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-2 fade-up">Kids Corner</h1>
        <p className="text-slate-300 mt-2">Safe, colourful shows picked just for you.</p>

        {featured && (
          <div
            onClick={() => navigate(`/watch/${featured.id}`)}
            className="mt-8 relative rounded-3xl overflow-hidden cursor-pointer group h-56 sm:h-72 border border-white/10"
          >
            <img src={featured.backdrop} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#10245c] via-[#10245c]/40 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-10 max-w-lg">
              <span className="text-xs tracking-widest text-[var(--nik-gold)] font-bold">FEATURED</span>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white mt-1">{featured.title}</h2>
              <p className="text-sm text-slate-200 mt-2 line-clamp-2">{featured.description}</p>
              <button className="mt-4 w-fit flex items-center gap-2 bg-[var(--nik-gold)] text-black font-bold px-6 py-2.5 rounded-full hover:scale-105 transition-transform">
                <Play size={18} fill="currentColor" /> Watch
              </button>
            </div>
          </div>
        )}

        <h3 className="font-display text-2xl font-bold text-white mt-10 mb-4">All Shows</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 pb-12">
          {items === null
            ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-video rounded-2xl skeleton" />)
            : items.map((c) => (
                <div
                  key={c.id}
                  data-testid="kids-card"
                  onClick={() => navigate(`/watch/${c.id}`)}
                  className="relative rounded-2xl overflow-hidden cursor-pointer group border-2 border-white/10 hover:border-[var(--nik-gold)] transition-colors"
                >
                  <img src={c.backdrop} alt={c.title} className="w-full aspect-video object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-[var(--nik-gold)] flex items-center justify-center">
                      <Play size={24} className="text-black ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 p-3">
                    <div className="font-display font-bold text-white text-sm">{c.title}</div>
                    <div className="text-[10px] text-slate-300">{c.languages?.join(" · ")}</div>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
